<template>
  <div class="standard-injection">
    <InjectionHeaderComponent
      :label="'Standard injection'"
      :loading="updated !== true"
      :injection="injection"
      :injectionSocket="injectionSocket"
      :isStandardInjection="true"
    />

    <InjectionErrors :error="injection.error" :method="method" />

    <div class="standard-injection__information">
      <DeviceMethodColumnComponent
        :configuration="injection.configuration"
        :vial="injection.vial"
        :postprocessings="postprocessings"
        :method="method"
        :methodOverride="injection.method_override"
        :column="injectionData.column"
        :device="injectionData.device"
        :sample="standard"
      />
      <Liquids
        v-if="isInjectionInProgress && injectionData.device.id"
        :deviceId="injectionData.device.id"
        :isCromite="injectionData.device.model === 'cmt'"
        :method="method"
        class="standard-injection__liquids"
      />
    </div>

    <div
      v-if="compoundEstimationError != null"
      class="standard-injection__compound-estimation-error"
      style="margin: 24px 32px 0 32px"
    >
      <span>{{ compoundEstimationError }}</span>
    </div>

    <template v-if="injection.state === 'NEW'">
      <not-available-component class="mt-8">
        <div>Did not started</div>
      </not-available-component>
    </template>
    <template v-else-if="injection.state === 'PEN' || injection.state === 'PRE'">
      <RunSubstate
        v-if="injection.substate != null"
        :substate="injection.substate"
        style="margin: 64px 0 40px 0"
      />
      <nothing-there-component v-else style="margin-top: 32px">
        No recorded data
      </nothing-there-component>
    </template>
    <template v-else>
      <div class="standard-injection__wrapper-panel-control">
        <ChromatogramControl
          v-model="config"
          :measurements="measurementsSelectable"
          :pressure="pressure"
          :isInjectionRunning="injection.state === 'RUN'"
          :hasGradient="hasGradient"
          :hasPostprocessing="hasPostprocessing"
          class="standard-injection__panel-control"
        />
      </div>

      <ChartGradient
        v-if="config.isShowGradient && method.gradient"
        :gradient="method.gradient"
        :injectionMinutes="effectiveRunTimeMin"
        class="standard-injection__chart-gradient"
      />

      <div
        v-if="measurementsSelectedFiltered.length && !isLoadingPostprocessings"
        style="margin-top: 16px"
      >
        <NewChartChromatogram
          :measurements="measurementsSelectedFiltered"
          :isShowPressure="config.isShowPressure"
          :measurementPressure="measurementPressure"
          :method="method"
          :injection="injection"
          :doPerformanceOptimisations="isInjectionRunning"
          :hasToolbar="!isInjectionRunning && !standard.archived && !injection.archived"
          :injectionSocket="injectionSocket"
          :timeMax="effectiveRunTimeMin"
          :initialZoom.sync="initialZoom"
          :creationDate="new Date(injection.created)"
          :isApplyPostprocessing="config.isApplyPostprocessing && !isInjectionInProgress"
          :postprocessings="postprocessings"
          :hasZoomActivationOverlay="hasZoomActivationOverlay"
          :editable="false"
          :hasPostprocessingsToApply="hasPostprocessingsToApply"
          :canRemovePeak="isSequenceCalibrationMode"
          @currentPressure="setCurrentPressure"
          @update="$emit('update')"
          @applyPostprocessing="setIsApplyPostprocessing"
          @zoomActivation="activateZoom"
          @liveUpdate="resetZoom"
        >
          <template #peaks="{ peaks, setPeakHighlighting, highlightedPeakId }">
            <StandardPeaksComponent
              v-if="!standard.archived && !injection.archived"
              :ppeaks="peaks"
              :injection-socket="injectionSocket"
              :highlightedPeakId="highlightedPeakId"
              @selection="setPeakHighlighting"
            />
          </template>
        </NewChartChromatogram>
      </div>

      <LoadingComponent v-else-if="updated == null || isLoadingPostprocessings" />
      <NothingThereComponent v-else style="margin-top: 32px">
        No measurement
      </NothingThereComponent>
    </template>
  </div>
</template>

<script>
  import NothingThereComponent from 'components/element/NothingThereComponent.vue';
  import LoadingComponent from 'components/element/LoadingComponent.vue';
  import NotAvailableComponent from 'components/element/NotAvailableComponent.vue';
  import StandardPeaksComponent from 'components/block/StandardPeaksComponent.vue';
  import InjectionHeaderComponent from 'components/block/InjectionHeaderComponent.vue';
  import { isPrevDateOlder } from 'utils/utils.ts';
  import InjectionErrors from 'components/element/InjectionErrors';
  import ChromatogramControl from '@/uikitProject/chromatogram/panels/vueChromatogramControl/ChromatogramControl';
  import ChartGradient from '@/uikitProject/charts/vueChartGradient/ChartGradient';
  import RunSubstate from '@/uikitProject/states/packs/RunSubstate';
  import { consoleHelpers } from 'utils/logHelpers';
  import Liquids from 'components/blocks/injections/liquids/Liquids';
  import { sendDebugEvent } from 'utils/sentryHelpers';
  import faviconService, { FaviconStatuses } from '@/services/FaviconService';
  import { INJECTION_STATES } from '@/constants/injections/states';
  import NewChartChromatogram from '@/components/blocks/charts/chromatogramNew/NewChartChromatogram.vue';
  import { apiMeasurements } from '@/api/graphql/cloud/measurements';
  import DeviceMethodColumnComponent from '@/components/element/DeviceMethodColumnComponent.vue';
  import SampleInjectionSocket from '@/api/sockets/SampleInjectionSocket';
  import { CHECK_CONNECTION_INTERVAL_MS } from '@/constants/injections/connection';
  import { apiInjections } from '@/api/graphql/cloud/injections';
  import { COMPOUND_ESTIMATION_ERRORS } from '@/constants/postprocessing/compoundEstimationErrors';

  const DEBUG_TASK_MEASUREMENT_CAN_BE_UNDEFINED =
    'https://gitlab.com/hplc-cloud-group/hplc-cloud-server/-/issues/1841';

  export default {
    name: 'StandardInjectionComponent',

    components: {
      DeviceMethodColumnComponent,
      NewChartChromatogram,
      Liquids,
      RunSubstate,
      ChartGradient,
      ChromatogramControl,
      InjectionErrors,
      NotAvailableComponent,
      LoadingComponent,
      StandardPeaksComponent,
      NothingThereComponent,
      InjectionHeaderComponent,
    },

    props: {
      injectionID: Number,
      injectionData: Object,
      standard: Object,
      calibrationUnit: {
        type: String,
        default: null,
      },
    },

    data() {
      const { id } = this.injectionData.injection;

      return {
        id,
        injection: this.injectionData.injection,
        method: this.injectionData.method,
        unit: this.calibrationUnit,
        measurement: null,
        updated: null,
        timerConnectionLost: null,

        measurementsSelected: [],

        config: {
          measurementsSelectedIds: [],
          isShowGradient: false,
          isShowPressure: false,
          isApplyPostprocessing: false,
        },

        injectionSocket: this.createInjectionSocket(id),
        listenersInjectionSocketGroupId: null,
        isLoadingInjection: false,

        wasInProgressRecently: false,

        initialZoom: null,

        postprocessings: null,

        hasZoomActivationOverlay: true,

        currentPressure: null,

        subscriptionForInjection: null,
        subscriptionForInjectionProgress: null,
        subscriptionForPostprocessings: null,

        isLoadingPostprocessings: true,
      };
    },

    computed: {
      configuration() {
        return this.injectionData?.injection?.configuration ?? {};
      },

      measurements() {
        return this.injection.measurements;
      },
      measurementPressure() {
        return Object.values(this.injection.measurements).find(
          (measurement) => measurement?.type === 'PRS',
        );
      },
      measurementsSelectable() {
        return Object.values(this.injection.measurements).reduce((measurements, measurement) => {
          if (!measurement) {
            sendDebugEvent('#1841: Measurement is undefined', [
              ['All measurements', this.injection.measurements],
              ['Task', DEBUG_TASK_MEASUREMENT_CAN_BE_UNDEFINED],
            ]);
          }

          if (measurement && measurement?.type !== 'PRS') {
            measurements[measurement.id] = measurement;
          }
          return measurements;
        }, {});
      },

      isPressureOutOfBounds() {
        if (this.currentPressure != null) {
          const { pressure_min, pressure_max } = this.injectionData.method;

          const isOutOfMin = pressure_min != null && this.currentPressure < pressure_min;
          const isOutOfMax = pressure_max != null && this.currentPressure > pressure_max;

          return this.isInjectionRunning && (isOutOfMin || isOutOfMax);
        }
        return false;
      },

      pressure() {
        if (this.measurementPressure) {
          return {
            current: this.currentPressure?.toFixed(),
            isOutOfBounds: this.isPressureOutOfBounds,
          };
        }
        return null;
      },
      isInjectionRunning() {
        return this.injection.state === INJECTION_STATES.RUNNING;
      },
      isInjectionInProgress() {
        return ['PEN', 'PRE', 'RUN', 'FIN', 'EXE'].includes(this.injection.state);
      },
      effectiveRunTimeMin() {
        const runTimeMs = this.injection?.method_override?.run_time_ms ?? this.method?.run_time_ms;
        if (runTimeMs != null) {
          return runTimeMs / 1000 / 60;
        }
        return this.method?.run_time_min;
      },

      hasGradient() {
        return (
          this.configuration?.pump?.type === 'TWO SYRINGE GRADIENT' &&
          Boolean(this.method?.gradient?.length)
        );
      },

      hasPostprocessing() {
        return (
          this.postprocessings?.some(
            (postprocessing) =>
              postprocessing.measurement.processing_state !== 'error' &&
              this.measurementsSelectedFiltered.some(
                (measurement) =>
                  measurement.id === postprocessing.measurement.id &&
                  measurement.processed_data_url != null,
              ),
          ) ?? false
        );
      },

      measurementsSelectedFiltered() {
        return this.measurementsSelected.filter(Boolean);
      },

      hasPostprocessingsToApply() {
        return (
          this.standard?.sequence?.postprocessing_id != null || this.postprocessings?.length > 0
        );
      },

      isSequenceCalibrationMode() {
        return this.sample?.sequence?.is_calibration_mode ?? false;
      },

      compoundEstimationError() {
        return (
          COMPOUND_ESTIMATION_ERRORS[
            this.injectionData?.injection?.compoundEstimationErrors?.[0]?.error_type
          ] ?? null
        );
      },
    },

    watch: {
      'config.measurementsSelectedIds'(ids) {
        setTimeout(() => {
          this.measurementsSelected = ids.map((id) => this.injection.measurements[id]);
        }, 0);
      },
      injectionData(injectionData) {
        if (injectionData == null) throw Error('injectionData should not be null');

        const idChanged = this.id !== injectionData.injection.id;

        if (idChanged) {
          this.hasZoomActivationOverlay = true;
        }

        const prevDateOlder = isPrevDateOlder(
          injectionData.injection.date_synced,
          this.injection.date_synced,
        );

        if (!idChanged && !prevDateOlder) {
          consoleHelpers.warn('Ignoring injectionData');
          return;
        }

        this.updated = null;

        this.id = injectionData.injection.id;
        const measurementsOld = this.injection.measurements;
        this.injection = injectionData.injection;

        if (idChanged) {
          this.initMeasurementsSelected(measurementsOld);

          this.isLoadingPostprocessings = true;

          this.initInjection(this.id);
          this.injectionSocket = this.createInjectionSocket(this.id);
        }
      },
      calibrationUnit(newUnit) {
        this.unit = newUnit;
      },
      isInjectionRunning: {
        handler(value) {
          if (value && this.measurementPressure) {
            this.config.isShowPressure = true;
          }
        },
        immediate: true,
      },
      isInjectionInProgress: {
        handler(value) {
          if (value) {
            faviconService.setStatus(FaviconStatuses.RUNNING);
          }
        },
        immediate: true,
      },

      measurementsSelectedFiltered() {
        if (this.config.isApplyPostprocessing) {
          const hasAtLeastOneMeasurementWithPostprocessing = this.postprocessings?.some(
            (postprocessing) =>
              postprocessing.measurement.processing_state !== 'error' &&
              this.measurementsSelectedFiltered.some(
                (measurement) => measurement.id === postprocessing.measurement.id,
              ),
          );

          if (!hasAtLeastOneMeasurementWithPostprocessing) {
            this.config.isApplyPostprocessing = false;
          }
        }
      },
    },

    created() {
      this.initInjection(this.id);
    },

    beforeDestroy() {
      this.stopSubscriptions();

      if (this.timerConnectionLost != null) clearTimeout(this.timerConnectionLost);
      this.destroyInjectionSocket();

      faviconService.setStatus(FaviconStatuses.NONE);
    },

    methods: {
      initMeasurementsSelected(_measurementsOld) {
        const measurementsNew = Object.values(this.injection.measurements);
        const measurementsOld = Object.values(_measurementsOld);

        const isSelectedAllMeasurements =
          measurementsOld.length === this.measurementsSelected.length;

        const measurementsSelectedTypes = this.measurementsSelected.map(
          (measurement) => measurement?.type,
        );
        const measurementsSelectedBasedOnPrevChoice = measurementsNew.filter((measurement) =>
          measurementsSelectedTypes.includes(measurement?.type),
        );

        if (isSelectedAllMeasurements && measurementsOld.length > 1) {
          this.config.measurementsSelectedIds = measurementsNew.map(({ id }) => id);
          this.measurementsSelected = measurementsNew;
        } else if (measurementsSelectedBasedOnPrevChoice.length) {
          this.config.measurementsSelectedIds = measurementsSelectedBasedOnPrevChoice.map(
            ({ id }) => id,
          );
          this.measurementsSelected = measurementsSelectedBasedOnPrevChoice;
        } else {
          const defaultMeasurementName = this.method.default_measurement ?? 'UV';
          // Analog detector's name is null. So I need to use its type field
          const defaultMeasurementType =
            this.method.default_measurement === 'Analog signal' && 'AD';

          const defaultMeasurement =
            measurementsNew.find((measurement) => {
              return (
                measurement.name === defaultMeasurementName ||
                measurement.type.startsWith(defaultMeasurementType)
              );
            }) ?? this.injection.measurements[this.injection.primary_measurement_id];

          if (defaultMeasurement) {
            this.config.measurementsSelectedIds = [defaultMeasurement.id];
            this.measurementsSelected = [defaultMeasurement];
          }
        }
      },

      createInjectionSocket(id) {
        this.destroyInjectionSocket();
        return SampleInjectionSocket.start(id);
      },
      destroyInjectionSocket() {
        if (this.injectionSocket != null) {
          this.injectionSocket.close(this.listenersInjectionSocketGroupId);
        }
      },

      onInjection(i) {
        if (this.injectionData.injection.id !== i.id) {
          return;
        }

        const measurementsOld = this.injection.measurements;
        this.injection = {
          ...this.injection,
          ...i,
        };
        // eslint-disable-next-line vue/no-mutating-props
        this.injectionData.injection = i;

        this.initMeasurementsSelected(measurementsOld);

        const hasPostprocessedSelected =
          this.postprocessings?.some(
            (postprocessing) =>
              postprocessing.measurement.processing_state !== 'error' &&
              this.measurementsSelectedFiltered.some(
                (measurement) =>
                  measurement.id === postprocessing.measurement.id &&
                  measurement.processed_data_url != null,
              ),
          ) ||
          this.measurementsSelectedFiltered.some(
            (measurement) => measurement?.processed_data_url != null,
          );

        if (hasPostprocessedSelected) {
          this.config.isApplyPostprocessing = true;
        }

        this.updated = true;
      },

      setIsApplyPostprocessing(isApplyPostprocessing) {
        this.config.isApplyPostprocessing = isApplyPostprocessing;
      },

      resetZoom() {
        this.initialZoom = null;
      },

      activateZoom() {
        this.hasZoomActivationOverlay = false;
      },

      setCurrentPressure(currentPressure) {
        this.currentPressure = currentPressure;
      },

      onEvent() {
        if (this.timerConnectionLost != null) {
          clearTimeout(this.timerConnectionLost);
        }

        if (this.isInjectionInProgress) {
          this.wasInProgressRecently = true;
          this.timerConnectionLost = setTimeout(
            () => this.setNoInternetState(),
            CHECK_CONNECTION_INTERVAL_MS,
          );
        }
      },

      setNoInternetState() {
        this.injection.state = '@';
      },

      async initInjection(injectionId) {
        this.stopSubscriptions();
        await this.initSubscriptionForInjection(injectionId);
        this.initSubscriptionForInjectionProgress(injectionId);
        this.initSubscriptionForPostprocessings(injectionId);
      },
      stopSubscriptions() {
        this.subscriptionForInjection?.unsubscribe();
        this.subscriptionForInjectionProgress?.unsubscribe();
        this.subscriptionForPostprocessings?.unsubscribe();
      },

      initSubscriptionForInjection(injectionId) {
        const { promise, resolve } = Promise.withResolvers();
        this.subscriptionForInjection = apiInjections.subscribeForInjection(
          injectionId,
          (injection) => {
            console.warn('event injection', injection);
            this.onInjection(injection);
            this.onEvent();
            resolve();
          },
        );

        return promise;
      },

      initSubscriptionForInjectionProgress(injectionId) {
        this.subscriptionForInjectionProgress = apiInjections.subscribeForInjectionProgress(
          injectionId,
          (injectionProgress) => {
            console.warn('event injectionProgress', injectionProgress);

            if (this.injectionData.injection.id !== injectionProgress.id) {
              return;
            }

            this.injection.state = injectionProgress.state;
            this.injection.substate = injectionProgress.substate;
            this.injection.progress = injectionProgress.progress;
            this.injection.progress_time_str = injectionProgress.progress_time_str;
            this.injection.date_synced = injectionProgress.date_synced;
            this.injection.elapsed_time_ms = injectionProgress.elapsed_time_ms;
            this.injection.expected_duration_ms = injectionProgress.expected_duration_ms;

            // eslint-disable-next-line vue/no-mutating-props
            this.injectionData.injection = this.injection;

            this.onEvent();
          },
        );
      },

      async initSubscriptionForPostprocessings(injectionId) {
        this.isLoadingPostprocessings = true;

        try {
          const { promise, resolve } = Promise.withResolvers();

          this.subscriptionForPostprocessings = apiMeasurements.subscribeForPostprocessings(
            injectionId,
            (postprocessings) => {
              this.postprocessings = postprocessings;
              this.config.isApplyPostprocessing = this.postprocessings?.some(
                (postprocessing) =>
                  postprocessing.measurement.processing_state !== 'error' &&
                  this.measurementsSelectedFiltered.some(
                    (measurement) =>
                      measurement.id === postprocessing.measurement.id &&
                      measurement.processed_data_url != null,
                  ),
              );

              resolve();
            },
          );

          await promise;
        } finally {
          this.isLoadingPostprocessings = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .standard-injection {
    &__wrapper-panel-control {
      display: flex;
      justify-content: center;
      margin: 48px 0 8px;

      @media (max-width: $screen-xs-max) {
        padding-top: 16px;
        overflow-x: auto;
        justify-content: flex-start;

        &::before,
        &::after {
          content: '';
          display: block;
          width: 16px;
          flex: none;
        }
      }
    }

    &__panel-control {
      margin: auto auto 24px;
    }

    &__chart-gradient {
      padding: 0 32px;
      margin-bottom: 20px;

      @media (max-width: $screen-sm-max) {
        padding: 0 10px;
      }
    }

    &__information {
      margin-top: 24px;
      padding: 0 32px;

      @media (max-width: $screen-sm-max) {
        padding: 0 16px;
      }
    }

    &__liquids {
      min-width: 280px;
      margin: 20px 0;
    }

    &__compound-estimation-error {
      background-color: #fee2e2;
      border: 1px solid #fecaca;
      color: $color-text-danger;
      padding: 12px 16px;
      border-radius: $border-radius__md;
      margin-top: 12px;
      font-size: $size-xs;
    }
  }
</style>
